import {
  Avatar,
  Button,
  Divider,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  IconButton,
  SelectionList,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ReadSearchApiArg, SearchFilterCondition } from 'lib-search-types';
import { SearchFilterLayout } from 'lib-search-ui';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { rolesApi } from 'apis/roles';
import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { getUserDisplayName } from 'utils/user';

import { FilterSearchInput } from './components';
import styles from './searchControls.module.scss';
import { hasActiveFilters } from './utils';

export const statuses = [
  { label: 'Working agents', value: 'working' },
  { label: 'Away agents', value: 'away' },
  { label: 'Online agents', value: 'online' },
  { label: 'Offline agents', value: 'offline' },
  { label: 'Wrap-up agents', value: 'wrap-up' },
  { label: 'Favorite agents', value: 'favorite' },
  { label: 'Assigned agents', value: 'assigned' },
  { label: 'Unassigned agents', value: 'unassigned' },
] as const;

export type Status = (typeof statuses)[number]['value'];

export const VALID_STATUSES: readonly Status[] = statuses.map((s) => s.value);

export type ActiveFilters = {
  status: Status | null;
  team: string[];
  agent: string[];
  role: string[];
  queue: string[];
};

type SearchControlsProps = {
  activeFilters: ActiveFilters;
  setActiveFilters: Dispatch<SetStateAction<ActiveFilters>>;
  onRefreshMembers: () => void;
  isRefreshing: boolean;
};

export const SearchControls = ({
  activeFilters,
  setActiveFilters,
  onRefreshMembers,
  isRefreshing,
}: SearchControlsProps): JSX.Element => {
  const [roleOpened, setRoleOpened] = useState<boolean>(false);
  const [teamSearchQuery, setTeamSearchQuery] = useState<string>('');
  const [agentSearchQuery, setAgentSearchQuery] = useState<string>('');
  const [queueSearchQuery, setQueueSearchQuery] = useState<string>('');

  const teamSearchArg: ReadSearchApiArg<'teams'> = useMemo(
    () => ({
      scope: 'teams',
      query: teamSearchQuery || undefined,
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'is_deactivated',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
          ...(activeFilters.agent.length > 0
            ? ([
                {
                  field: 'agents',
                  operator: 'includes_one',
                  values: activeFilters.agent.map((agentId) => ({
                    value: agentId,
                  })),
                },
              ] satisfies SearchFilterCondition<'teams'>[])
            : []),
          ...(activeFilters.queue.length > 0
            ? ([
                {
                  field: 'queues',
                  operator: 'includes_one',
                  values: activeFilters.queue.map((queueId) => ({
                    value: queueId,
                  })),
                },
              ] satisfies SearchFilterCondition<'teams'>[])
            : []),
        ],
      },
      sortBy: { field: 'name', order: 'asc' },
    }),
    [teamSearchQuery, activeFilters.agent, activeFilters.queue],
  );

  const agentSearchArg = useMemo(
    (): ReadSearchApiArg<'users'> => ({
      scope: 'users',
      query: agentSearchQuery || undefined,
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'user_type',
            operator: 'is_one',
            values: [{ value: 'member' }],
          },
          ...(activeFilters.team.length > 0
            ? ([
                {
                  field: 'teams',
                  operator: 'includes_one',
                  values: activeFilters.team.map((teamId) => ({
                    value: teamId,
                  })),
                },
              ] satisfies SearchFilterCondition<'users'>[])
            : []),
          ...(activeFilters.queue.length > 0
            ? ([
                {
                  field: 'queues',
                  operator: 'includes_one',
                  values: activeFilters.queue.map((queueId) => ({
                    value: queueId,
                  })),
                },
              ] satisfies SearchFilterCondition<'users'>[])
            : []),
        ],
      },
      sortBy: { field: 'display_name', order: 'asc' },
    }),
    [agentSearchQuery, activeFilters.team, activeFilters.queue],
  );

  const [readSearchTeams, { results: teams, ...teamsQueryResult }] =
    useLazyReadSearchInfiniteQuery<'teams'>();

  const [readSearchAgents, { results: agents, ...agentsQueryResult }] =
    useLazyReadSearchInfiniteQuery<'users'>();

  const queueSearchArg: ReadSearchApiArg<'queues'> = useMemo(
    () => ({
      scope: 'queues',
      query: queueSearchQuery || undefined,
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'is_archived',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
          ...(activeFilters.agent.length > 0
            ? ([
                {
                  field: 'agents',
                  operator: 'includes_one',
                  values: activeFilters.agent.map((agentId) => ({
                    value: agentId,
                  })),
                },
              ] satisfies SearchFilterCondition<'queues'>[])
            : []),
          ...(activeFilters.team.length > 0
            ? ([
                {
                  field: 'teams',
                  operator: 'includes_one',
                  values: activeFilters.team.map((teamId) => ({
                    value: teamId,
                  })),
                },
              ] satisfies SearchFilterCondition<'queues'>[])
            : []),
        ],
      },
      sortBy: { field: 'name', order: 'asc' },
    }),
    [queueSearchQuery, activeFilters.agent, activeFilters.team],
  );

  const [readSearchQueues, { results: queues, ...queuesQueryResult }] =
    useLazyReadSearchInfiniteQuery<'queues'>();

  useUpdateEffect(() => {
    readSearchTeams(teamSearchArg, { debounceTime: 300 });
  }, [readSearchTeams, teamSearchArg, teamSearchQuery]);

  useUpdateEffect(() => {
    readSearchAgents(agentSearchArg, { debounceTime: 300 });
  }, [readSearchAgents, agentSearchArg]);

  useUpdateEffect(() => {
    readSearchQueues(queueSearchArg, { debounceTime: 300 });
  }, [readSearchQueues, queueSearchArg, queueSearchQuery]);

  const handleTeamDropdownToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        readSearchTeams(teamSearchArg);
      } else {
        setTeamSearchQuery('');
      }
    },
    [readSearchTeams, teamSearchArg],
  );

  const handleAgentDropdownToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        readSearchAgents(agentSearchArg);
      } else {
        setAgentSearchQuery('');
      }
    },
    [readSearchAgents, agentSearchArg],
  );

  const handleQueueDropdownToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        readSearchQueues(queueSearchArg);
      } else {
        setQueueSearchQuery('');
      }
    },
    [readSearchQueues, queueSearchArg],
  );

  // Roles query
  const { systemRoles, customRoles, rolesLoading } = rolesApi.useReadRolesQuery(
    undefined,
    {
      skip: !roleOpened,
      selectFromResult: ({ data, isFetching }) => {
        const roles = data?.data.roles ?? [];
        return {
          systemRoles: roles
            .filter((role) => role.is_system_role)
            .sort((a, b) => a.name.localeCompare(b.name)),
          customRoles: roles
            .filter((role) => !role.is_system_role)
            .sort((a, b) => a.name.localeCompare(b.name)),
          rolesLoading: isFetching,
        };
      },
    },
  );

  const handleClearFilters = useCallback(() => {
    setActiveFilters({
      status: null,
      team: [],
      agent: [],
      role: [],
      queue: [],
    });
  }, [setActiveFilters]);

  return (
    <div className={styles.controls}>
      {hasActiveFilters(activeFilters) && (
        <Button variant="tertiary" icon="close" onClick={handleClearFilters}>
          Clear filters
        </Button>
      )}

      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
        }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <SearchFilterLayout>
                <SearchFilterLayout.Row scrollable noPadding>
                  <SelectionList
                    isMultiple={false}
                    selectedValues={
                      activeFilters.status ? [activeFilters.status] : []
                    }
                    onChange={(e) => {
                      const newStatus = e.selectedValues[0] as
                        | Status
                        | undefined;
                      setActiveFilters((prev) => ({
                        ...prev,
                        status:
                          newStatus === prev.status
                            ? null
                            : (newStatus ?? null),
                      }));
                      toggleDropdown();
                    }}
                    defaultActiveItemIndex={0}
                  >
                    {statuses.map((status) => (
                      <SearchFilterLayout.Item
                        key={status.value}
                        value={status.value}
                        label={status.label}
                      />
                    ))}
                  </SelectionList>
                </SearchFilterLayout.Row>

                <SearchFilterLayout.Row borderTop>
                  <Button
                    variant="secondary"
                    disabled={!activeFilters.status}
                    onClick={() => {
                      setActiveFilters((prev) => ({
                        ...prev,
                        status: null,
                      }));
                      toggleDropdown();
                    }}
                  >
                    Clear
                  </Button>
                </SearchFilterLayout.Row>
              </SearchFilterLayout>
            )}
          </DropdownButtonContext.Consumer>
        }
      >
        {activeFilters.status
          ? statuses.find((s) => s.value === activeFilters.status)?.label
          : 'Status'}
      </DropdownButton>

      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.role.length > 0
              ? `${activeFilters.role.length}`
              : undefined,
        }}
        onToggle={setRoleOpened}
        dropdown={
          <SearchFilterLayout>
            <SearchFilterLayout.Row scrollable noPadding>
              <SelectionList
                selectedValues={activeFilters.role}
                isMultiple
                onChange={(e) =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    role: e.selectedValues,
                  }))
                }
                defaultActiveItemIndex={0}
              >
                {rolesLoading ? (
                  <SearchFilterLayout.Loading />
                ) : (
                  <>
                    {systemRoles.map((role) => (
                      <SearchFilterLayout.Item
                        key={role.id}
                        value={role.id}
                        label={role.name}
                        icon="role-system"
                      />
                    ))}

                    {systemRoles.length > 0 && customRoles.length > 0 && (
                      <Divider />
                    )}

                    {customRoles.map((role) => (
                      <SearchFilterLayout.Item
                        key={role.id}
                        value={role.id}
                        label={role.name}
                        icon="role-custom"
                      />
                    ))}
                  </>
                )}
              </SelectionList>
            </SearchFilterLayout.Row>

            <SearchFilterLayout.Row borderTop>
              <Button
                variant="secondary"
                disabled={!activeFilters.role.length}
                onClick={() =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    role: [],
                  }))
                }
              >
                Clear
              </Button>
            </SearchFilterLayout.Row>
          </SearchFilterLayout>
        }
      >
        <FlexBox alignItems="center" gap="100">
          Role
        </FlexBox>
      </DropdownButton>

      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.team.length > 0
              ? `${activeFilters.team.length}`
              : undefined,
        }}
        destroyOnClose
        onToggle={handleTeamDropdownToggle}
        dropdown={
          <SearchFilterLayout>
            <SearchFilterLayout.Row borderBottom>
              <FilterSearchInput
                value={teamSearchQuery}
                onChange={setTeamSearchQuery}
                placeholder="Search teams..."
              />
            </SearchFilterLayout.Row>

            <SearchFilterLayout.Row scrollable noPadding>
              <SelectionList
                selectedValues={activeFilters.team}
                isMultiple
                onChange={(e) =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    team: e.selectedValues,
                  }))
                }
                defaultActiveItemIndex={0}
              >
                {teams.map((team) => (
                  <SearchFilterLayout.Item
                    key={team.id}
                    value={team.id}
                    label={team.name}
                    highlight={teamSearchQuery}
                  />
                ))}

                {teamsQueryResult.isFetching && <SearchFilterLayout.Loading />}
                {teamsQueryResult.isSuccess && !teams.length && (
                  <SearchFilterLayout.Empty />
                )}

                {teamsQueryResult.hasNextPage &&
                  !teamsQueryResult.isFetching && (
                    <Waypoint onEnter={teamsQueryResult.fetchNextPage} />
                  )}
              </SelectionList>
            </SearchFilterLayout.Row>

            <SearchFilterLayout.Row borderTop>
              <Button
                variant="secondary"
                disabled={!activeFilters.team.length}
                onClick={() =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    team: [],
                  }))
                }
              >
                Clear
              </Button>
            </SearchFilterLayout.Row>
          </SearchFilterLayout>
        }
      >
        Team
      </DropdownButton>

      <DropdownButton
        data-testid="agent-filter"
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.agent.length > 0
              ? `${activeFilters.agent.length}`
              : undefined,
        }}
        destroyOnClose
        onToggle={handleAgentDropdownToggle}
        dropdown={
          <SearchFilterLayout>
            <SearchFilterLayout.Row borderBottom>
              <FilterSearchInput
                value={agentSearchQuery}
                onChange={setAgentSearchQuery}
                placeholder="Search agents..."
                data-testid="agent-filter-search"
              />
            </SearchFilterLayout.Row>

            <SearchFilterLayout.Row scrollable noPadding>
              <SelectionList
                selectedValues={activeFilters.agent}
                isMultiple
                onChange={(e) =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    agent: e.selectedValues,
                  }))
                }
                defaultActiveItemIndex={0}
              >
                {agents.map((agent) => (
                  <SearchFilterLayout.Item
                    key={agent.id}
                    value={agent.id}
                    label={getUserDisplayName(agent)}
                    highlight={agentSearchQuery}
                    icon={<Avatar user={agent} size="24" />}
                  />
                ))}

                {agentsQueryResult.isFetching && <SearchFilterLayout.Loading />}
                {agentsQueryResult.isSuccess && !agents.length && (
                  <SearchFilterLayout.Empty />
                )}

                {agentsQueryResult.hasNextPage &&
                  !agentsQueryResult.isFetching && (
                    <Waypoint onEnter={agentsQueryResult.fetchNextPage} />
                  )}
              </SelectionList>
            </SearchFilterLayout.Row>

            <SearchFilterLayout.Row borderTop>
              <Button
                variant="secondary"
                disabled={!activeFilters.agent.length}
                onClick={() =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    agent: [],
                  }))
                }
              >
                Clear
              </Button>
            </SearchFilterLayout.Row>
          </SearchFilterLayout>
        }
      >
        Agent
      </DropdownButton>

      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.queue.length > 0
              ? `${activeFilters.queue.length}`
              : undefined,
        }}
        destroyOnClose
        onToggle={handleQueueDropdownToggle}
        dropdown={
          <SearchFilterLayout>
            <SearchFilterLayout.Row borderBottom>
              <FilterSearchInput
                value={queueSearchQuery}
                onChange={setQueueSearchQuery}
                placeholder="Search queues..."
              />
            </SearchFilterLayout.Row>

            <SearchFilterLayout.Row scrollable noPadding>
              <SelectionList
                selectedValues={activeFilters.queue}
                isMultiple
                onChange={(e) =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    queue: e.selectedValues,
                  }))
                }
                defaultActiveItemIndex={0}
              >
                {queues.map((queue) => (
                  <SearchFilterLayout.Item
                    key={queue.id}
                    value={queue.id}
                    label={queue.name}
                    highlight={queueSearchQuery}
                  />
                ))}

                {queuesQueryResult.isFetching && <SearchFilterLayout.Loading />}
                {queuesQueryResult.isSuccess && !queues.length && (
                  <SearchFilterLayout.Empty />
                )}

                {queuesQueryResult.hasNextPage &&
                  !queuesQueryResult.isFetching && (
                    <Waypoint onEnter={queuesQueryResult.fetchNextPage} />
                  )}
              </SelectionList>
            </SearchFilterLayout.Row>

            <SearchFilterLayout.Row borderTop>
              <Button
                variant="secondary"
                disabled={!activeFilters.queue.length}
                onClick={() =>
                  setActiveFilters((prev) => ({
                    ...prev,
                    queue: [],
                  }))
                }
              >
                Clear
              </Button>
            </SearchFilterLayout.Row>
          </SearchFilterLayout>
        }
      >
        Queue
      </DropdownButton>

      <Tooltip content="Refresh">
        <IconButton
          icon="sync"
          variant="tertiary"
          isLoading={isRefreshing}
          onClick={onRefreshMembers}
        />
      </Tooltip>
    </div>
  );
};
